import { Button, Flex, Input, MenuItem, Stack, Text } from '@chakra-ui/react';
import { useState } from 'react';
import { LinkIcon } from 'lucide-react';
import { Editor, JSONContent } from '@tiptap/core';
import { test, tokenize } from 'linkifyjs';
import { chatMessageDBlock } from './ReactFlow/nodes/flowEditorBlockNodeTypes';
import { AnchoredPanel, AnchorRect } from './ReactFlow/nodes/blocknode/AnchoredPanel';

const PANEL_WIDTH = 320;

// The trigger and the panel are split so the panel can render OUTSIDE the
// add-block menu's MenuList, which closes (and, being lazy, unmounts) on select
// and would otherwise tear a panel rendered inside it straight back down.

export function EditLinkMenuItem({ onOpen }: { onOpen: () => void }) {
  return (
    <MenuItem onClick={onOpen} icon={<LinkIcon size="16px" />}>
      Link
    </MenuItem>
  );
}

type EditLinkModalButtonPropTypes = {
  editor: Editor;
  updateContent?: (newContent: JSONContent) => void;
  isOpen: boolean;
  onClose: () => void;
  anchorRect: AnchorRect | null;
};

/**
 * Panel for inserting a link. Render alongside (not inside) the add-block menu;
 * pair it with EditLinkMenuItem for the trigger. Inserts the link as a new
 * message bubble when updateContent is provided, otherwise inline into the
 * current editor selection.
 */
export function EditLinkModalButton({
  editor,
  updateContent,
  isOpen,
  onClose,
  anchorRect,
}: EditLinkModalButtonPropTypes) {
  const [text, setText] = useState('');
  const [link, setLink] = useState('');
  const [error, setError] = useState('');

  // Clear the form whenever the panel closes (submit, X, Escape or backdrop) so
  // a half-typed link does not linger when it reopens.
  const handleClose = () => {
    setText('');
    setLink('');
    setError('');
    onClose();
  };

  const handleAddLink = () => {
    if (link.length === 0) {
      setError('Enter a URL.');
      return;
    }
    if (!test(link)) {
      setError('Enter a valid URL.');
      return;
    }

    const displayText = text.length ? text : link;
    const firstToken = tokenize(link)[0];
    if (!firstToken) {
      setError('Enter a valid URL.');
      return;
    }
    const hrefValue = firstToken.toHref();
    // The Link extension always renders links as target=_blank with a safe rel,
    // so a new tab is the only meaningful target.
    const attrs = { href: hrefValue, target: '_blank', rel: 'noopener noreferrer nofollow' };

    if (updateContent) {
      const linkJSON: JSONContent = {
        type: 'paragraph',
        content: [{ type: 'text', text: displayText, marks: [{ type: 'link', attrs }] }],
      };
      updateContent(chatMessageDBlock(linkJSON));
    } else {
      editor
        .chain()
        .setLink(attrs)
        .focus()
        .insertContentAt(editor.state.selection.from, displayText)
        .run();
    }

    handleClose();
  };

  return (
    <AnchoredPanel
      isOpen={isOpen}
      onClose={handleClose}
      anchorRect={anchorRect}
      width={PANEL_WIDTH}
      title="Add link"
    >
      <Stack
        spacing={2}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleAddLink();
          }
        }}
      >
        <Input
          autoFocus
          size="sm"
          type="url"
          value={link}
          placeholder="https://example.com.au"
          onChange={(e) => {
            setLink(e.target.value.trim());
            setError('');
          }}
        />
        <Input
          size="sm"
          value={text}
          placeholder="Link text (optional)"
          onChange={(e) => setText(e.target.value)}
        />
        {error ? (
          <Text fontSize="sm" color="fg.error">
            {error}
          </Text>
        ) : null}
        <Flex justify="flex-end">
          <Button size="sm" onClick={handleAddLink}>
            Add
          </Button>
        </Flex>
      </Stack>
    </AnchoredPanel>
  );
}
